import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Instagram, Truck, Headphones, Lock, RotateCcw } from "lucide-react";
import { Logo } from "./Logo";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import PaymentTrust from "./PaymentTrust";
import { useTheme } from "../context/ThemeContext";

function TikTokIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.5 3c.3 2.02 1.46 3.62 3.5 3.86v2.4c-1.2.03-2.34-.28-3.5-.94v6.02c0 3.9-2.9 6.4-6.3 5.6-2.5-.58-4.2-2.85-4.2-5.4a5.6 5.6 0 0 1 6.5-5.53v2.46c-.34-.1-.7-.16-1.06-.16a3.1 3.1 0 1 0 3.1 3.1V3h1.96z"/>
    </svg>
  );
}

export function Footer() {
  const [settings, setSettings] = useState({ instagram: "", tiktok: "", contact_email: "contact@luxeoryvea.com" });
  const { t } = useLang();
  const { universe } = useTheme();
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  const igLink = settings.instagram || "#";
  const ttLink = settings.tiktok || "#";
  const email = settings.contact_email || "contact@luxeoryvea.com";
  return (
    <footer className="relative border-t pt-20 pb-10" style={{ background: "var(--lo-surface)", borderColor: "var(--lo-border)" }} data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 items-center pb-14 mb-14 border-b" style={{ borderColor: "var(--lo-border)" }} data-testid="footer-strip">
          {[[Truck, t("footer.fast_ship"), t("footer.fast_ship_sub")], [Headphones, t("footer.support"), t("footer.support_sub")]].map(([I, tt, s], i) => (
            <div key={i} className="flex items-center gap-3 justify-center md:justify-start">
              <I size={22} stroke="var(--lo-gold)" strokeWidth={1} />
              <div><p className="overline" style={{ fontSize: "0.55rem" }}>{tt}</p><p className="text-[10px]" style={{ color: "var(--lo-muted)" }}>{s}</p></div>
            </div>
          ))}
          <div className="hidden md:flex justify-center"><Logo size="sm" /></div>
          {[[Lock, t("footer.secure"), t("footer.secure_sub")], [RotateCcw, t("footer.returns"), t("footer.returns_sub")]].map(([I, tt, s], i) => (
            <div key={i} className="flex items-center gap-3 justify-center md:justify-end">
              <I size={22} stroke="var(--lo-gold)" strokeWidth={1} />
              <div><p className="overline" style={{ fontSize: "0.55rem" }}>{tt}</p><p className="text-[10px]" style={{ color: "var(--lo-muted)" }}>{s}</p></div>
            </div>
          ))}
        </div>
        <div className="flex justify-center mb-16"><Logo size="md" /></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div>
            <h4 className="overline mb-5">LUXE ORYVEA PREMIUM</h4>
            <ul className="space-y-3 text-sm" style={{ color: "var(--lo-muted)" }}>
              <li><Link to="/marque" className="link-underline">{t("footer.our_story")}</Link></li>
              <li><Link to="/formule" className="link-underline">{t("footer.our_formula")}</Link></li>
              <li><Link to="/contact" className="link-underline">{t("footer.contact")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="overline mb-5">{t("footer.help")}</h4>
            <ul className="space-y-3 text-sm" style={{ color: "var(--lo-muted)" }}>
              <li><Link to="/faq" className="link-underline">FAQ</Link></li>
              <li><Link to="/legal/livraison" className="link-underline">{t("footer.delivery")}</Link></li>
              <li><Link to="/legal/retours" className="link-underline">{t("footer.returns_link")}</Link></li>
              <li><Link to="/legal/cgv" className="link-underline">{t("footer.cgv")}</Link></li>
              <li><Link to="/legal/confidentialite" className="link-underline">{t("footer.privacy")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="overline mb-5">{t("footer.contact")}</h4>
            <ul className="space-y-3 text-sm" style={{ color: "var(--lo-muted)" }}>
              <li><Link to="/contact" className="link-underline">{t("footer.contact_form")}</Link></li>
              <li>{email}</li>
            </ul>
            <div className="flex gap-4 mt-6" data-testid="footer-socials">
              <a href={igLink} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="social-instagram" className="w-10 h-10 flex items-center justify-center border rounded-full hover:border-[var(--lo-rose)] transition-colors" style={{ borderColor: "var(--lo-border-rose)", color: "var(--lo-rose-hi)" }}><Instagram size={16} /></a>
              <a href={ttLink} target="_blank" rel="noreferrer" aria-label="TikTok" data-testid="social-tiktok" className="w-10 h-10 flex items-center justify-center border rounded-full hover:border-[var(--lo-rose)] transition-colors" style={{ borderColor: "var(--lo-border-rose)", color: "var(--lo-rose-hi)" }}><TikTokIcon size={16} /></a>
            </div>
          </div>
          <div>
            <h4 className="overline mb-5">{t("footer.ritual")}</h4>
            <p className="text-sm leading-relaxed" style={{ color: "var(--lo-muted)" }}>
              {t("footer.ritual_text")}
            </p>
            <Link to={universe === "femme" ? "/serum-femme" : "/serum-homme"} className="btn-ghost inline-flex items-center justify-center h-11 px-6 mt-6">{t("common.discover_serum")}</Link>
          </div>
        </div>
        <div className="gold-line mb-8" />
        <PaymentTrust />
        <p className="text-center text-xs tracking-widest" style={{ color: "var(--lo-muted)" }}>
          © {new Date().getFullYear()} LUXE ORYVEA PREMIUM — {t("footer.rights")}
        </p>
      </div>
    </footer>
  );
}
